import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Box } from "@mui/material";
import { useWindowSize } from "@uidotdev/usehooks";
import SabiasQue from "../SabiasQue";
import SinConexionKawaii from "../SinConexionKawaii";
import { useMapFullscreen } from "../../context/MapFullscreenContext";
import { getGuideAccent } from "../../utils/guideAccent";

// Template338 (Edimburgo): mapa día 3 y National Museum
const Template338 = ({
	backgroundColor,
	textColor,
	headerBackgroundColor,
	headerTextColor,
	fontFamily,
	page,
}) => {
	const size = useWindowSize();
	const isSmallScreen = size.width <= 600;
	const [loaded, setLoaded] = useState(
		typeof navigator !== "undefined" ? navigator.onLine : true,
	);
	const [fullscreen, setFullscreen] = useState(false);
	const [isOnline, setIsOnline] = useState(
		typeof navigator !== "undefined" ? navigator.onLine : true,
	);
	const mapFullscreenContext = useMapFullscreen();
	const setMapFullscreen = mapFullscreenContext?.setMapFullscreen;

	const openFullscreen = () => {
		setFullscreen(true);
		if (typeof setMapFullscreen === "function") setMapFullscreen(true);
	};
	const closeFullscreen = () => {
		setFullscreen(false);
		if (typeof setMapFullscreen === "function") setMapFullscreen(false);
	};

	useEffect(() => {
		return () => {
			if (typeof setMapFullscreen === "function") setMapFullscreen(false);
		};
	}, [setMapFullscreen]);

	useEffect(() => {
		const handleOnline = () => {
			setIsOnline(true);
			setLoaded(true);
		};
		const handleOffline = () => {
			setIsOnline(false);
			setLoaded(true);
		};
		window.addEventListener("online", handleOnline);
		window.addEventListener("offline", handleOffline);
		return () => {
			window.removeEventListener("online", handleOnline);
			window.removeEventListener("offline", handleOffline);
		};
	}, []);

	const headerText = page.texts?.[0]?.content;
	const mapBubble = page.texts?.[1]?.content;
	const getCanvasImageUrl = (id) => {
		if (page.canvasImages && Array.isArray(page.canvasImages)) {
			const found = page.canvasImages.find((img) => img?.id === id);
			if (found && found.imageUrl) return found.imageUrl;
		}
		return undefined;
	};
	const getImageSrc = (id) => {
		if (page.images && Array.isArray(page.images)) {
			const found = page.images.find((img) => img?.id === id);
			if (found && found.src) return found.src;
		}
		return undefined;
	};

	const logoImage = getCanvasImageUrl("logo");
	const mapSticker =
		getCanvasImageUrl("bici-sticker") || getCanvasImageUrl("map-sticker");
	const mapUrl = page.maps?.[0];
	const reserva =
		page?.reserva ?? page?.data?.reserva ?? page?.data?.data?.reserva;
	const mapaOffline = (page.images || []).find(
		(img) => img?.id === "mapa-offline",
	)?.src;
	const sections = Array.isArray(page?.sections)
		? page.sections
		: Array.isArray(page?.data?.sections)
			? page.data.sections
			: [];
	const ui = page?.ui ?? page?.data?.ui ?? {};

	const actualHeaderColor = headerBackgroundColor || "#de5624";
	const accent = getGuideAccent(actualHeaderColor);
	const contentColor = textColor || "#444";

	const estiloH3 = {
		fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
		fontSize: 14,
		fontWeight: 700,
		textTransform: "uppercase",
		margin: 0,
	};

	const estiloP = {
		fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
		fontSize: 13,
		color: contentColor,
		margin: 0,
	};

	if (fullscreen) {
		const overlay = (
			<Box
				sx={{
					position: "fixed",
					top: 0,
					left: 0,
					right: 0,
					bottom: 0,
					backgroundColor: "#fff",
					zIndex: 999999,
					display: "flex",
					flexDirection: "column",
				}}
			>
				<Box
					sx={{
						padding: "10px",
						paddingTop: "calc(10px + env(safe-area-inset-top, 0px))",
						textAlign: "right",
						backgroundColor: actualHeaderColor,
						flexShrink: 0,
					}}
				>
					<Box
						component="button"
						onClick={closeFullscreen}
						sx={{
							backgroundColor: "#fff",
							color: actualHeaderColor,
							border: "none",
							borderRadius: "8px",
							padding: "8px 12px",
							fontWeight: 600,
							cursor: "pointer",
							fontFamily: `${fontFamily || "'Poppins', sans-serif"}`,
							WebkitAppearance: "none",
							appearance: "none",
						}}
					>
						{ui.backButton || "← Volver a la guía"}
					</Box>
				</Box>
				<Box sx={{ flex: 1, minHeight: 0, position: "relative" }}>
					<iframe
						title={ui.mapTitleFullscreen || "Mapa grande"}
						src={mapUrl}
						style={{
							border: 0,
							position: "absolute",
							inset: 0,
							width: "100%",
							height: "100%",
						}}
						allowFullScreen
						loading="lazy"
					></iframe>
				</Box>
			</Box>
		);
		return typeof document !== "undefined"
			? createPortal(overlay, document.body)
			: overlay;
	}

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				minHeight: "100%",
				height: "100%",
				width: "100%",
				backgroundColor: backgroundColor || "#f7f7f7",
				color: contentColor,
				position: "relative",
				fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
				"& *:not([data-template-header])": {
					fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
					color: "inherit",
				},
			}}
		>
			<Box
				data-block-id="header"
				data-template-header
				sx={{
					backgroundColor: actualHeaderColor,
					color: headerTextColor || "#fff",
					textAlign: "center",
					padding: "15px 0",
					fontFamily: `${fontFamily || "'Poppins', sans-serif"}`,
					fontSize: "14px",
					fontWeight: 700,
					flexShrink: 0,
					position: "sticky",
					top: 0,
					zIndex: 2,
				}}
			>
				{headerText}
				{logoImage && (
					<Box
						component="img"
						src={logoImage}
						alt="logo"
						sx={{
							width: isSmallScreen ? 66 : 80,
							position: "absolute",
							right: 2,
							top: 18,
							zIndex: 999999,
						}}
					/>
				)}
			</Box>

			<Box
				data-block-id="map"
				sx={{
					flex: 1,
					marginTop: "-70px",
					position: "relative",
					minHeight: 0,
					display: "flex",
					flexDirection: "column",
					minWidth: 0,
				}}
			>
				{!loaded && isOnline && (
					<Box
						sx={{
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							position: "absolute",
							top: 0,
							left: 0,
							right: 0,
							bottom: 0,
							zIndex: 1,
						}}
					>
						<Box
							sx={{
								width: 20,
								height: 20,
								border: "4px solid #ccc",
								borderTop: `4px solid ${actualHeaderColor}`,
								borderRadius: "50%",
								animation: "spin 1s linear infinite",
								"@keyframes spin": {
									"0%": { transform: "rotate(0deg)" },
									"100%": { transform: "rotate(360deg)" },
								},
							}}
						/>
					</Box>
				)}

				{isOnline && (
					<Box
						component="button"
						onClick={openFullscreen}
						sx={{
							position: "absolute",
							top: 80,
							right: 10,
							backgroundColor: actualHeaderColor,
							color: "#fff!important",
							padding: "8px 10px",
							fontSize: "11px",
							borderRadius: "8px",
							fontFamily: `${fontFamily || "'Poppins', sans-serif"}`,
							boxShadow: "1px 1px 2px rgba(0,0,0,0.2)",
							border: "none",
							cursor: "pointer",
							zIndex: 3,
						}}
					>
						{ui.fullscreenButton || "Ver mapa grande"}
					</Box>
				)}

				<Box sx={{ flex: 1, minHeight: 0, position: "relative" }}>
					{isOnline && mapUrl ? (
						<Box
							component="iframe"
							title={ui.mapTitleEmbed || "Mapa día 2"}
							src={mapUrl}
							width="100%"
							height="100%"
							sx={{
								border: 0,
								display: "block",
								position: "absolute",
								top: 0,
								left: 0,
								right: 0,
								bottom: 0,
							}}
							loading="lazy"
							onLoad={() => setLoaded(true)}
						/>
					) : mapaOffline ? (
						<img
							src={mapaOffline}
							alt=""
							style={{
								width: "100%",
								height: "100%",
								objectFit: "cover",
								position: "absolute",
								top: 0,
								left: 0,
							}}
						/>
					) : !isOnline ? (
						<SinConexionKawaii height="100%" fontFamily={fontFamily} />
					) : null}
				</Box>

				{mapSticker && (
					<img
						src={mapSticker}
						alt=""
						style={{
							width: isSmallScreen ? 90 : 110,
							position: "absolute",
							bottom: -10,
							left: -16,
							zIndex: 4,
						}}
					/>
				)}

				{(mapBubble || reserva?.url) && (
					<Box
						sx={{
							position: "absolute",
							left: 78,
							bottom: 78,
							backgroundColor: "#fff8db",
							border: "2px solid #f5c400",
							borderRadius: "16px 16px 16px 0px",
							padding: "8px 10px",
							fontSize: 11,
							fontFamily: `${fontFamily || "'Poppins', sans-serif"}`,
							color: contentColor,
							boxShadow: "2px 2px 4px rgba(0,0,0,0.15)",
							width: isSmallScreen ? 150 : 170,
							zIndex: 5,
							lineHeight: 1.35,
						}}
					>
						{mapBubble}{" "}
						{reserva?.url && (
							<a
								href={reserva.url}
								target="_blank"
								rel="noopener noreferrer"
								style={{
									color: "#000000",
									fontWeight: 700,
									textDecoration: "underline",
								}}
							>
								{reserva.text}
							</a>
						)}
					</Box>
				)}
			</Box>

			<Box
				sx={{
					padding: "10px 12px 12px",
					display: "flex",
					flexDirection: "column",
					gap: "12px",
					position: "relative",
					flexShrink: 0,
				}}
			>
				{sections.map((section, idx) => {
					if (section.layout === "box") {
						const isRightBox = section.align === "right";
						return (
							<Box
								key={idx}
								data-block-id={`text-${idx + 1}`}
								sx={{ position: "relative" }}
							>
								<Box
									component="p"
									sx={{
										...estiloP,
										padding: "10px 12px",
										backgroundColor: accent.boxBg,
										borderRadius: "12px",
										width: isRightBox ? "60%" : "100%",
										marginLeft: isRightBox ? "auto" : 0,
										boxSizing: "border-box",
									}}
								>
									{section.title && (
										<Box component="h3" sx={estiloH3}>
											{section.title}
										</Box>
									)}
									{section.body}
								</Box>
							</Box>
						);
					}

					if (section.layout === "sticker") {
						const stickerSrc = getCanvasImageUrl(section.stickerCanvasId);
						return (
							<Box
								key={idx}
								data-block-id={`text-${idx + 1}`}
								sx={{
									position: "relative",
									height: 54,
									overflow: "visible",
								}}
							>
								{stickerSrc && (
									<img
										src={stickerSrc}
										alt=""
										style={{
											width: 95,
											position: "absolute",
											right: -18,
											bottom: -8,
											zIndex: 5,
										}}
									/>
								)}
								{section.bubble && (
									<Box
										sx={{
											position: "absolute",
											right: 68,
											top: 4,
											backgroundColor: "#fff8db",
											border: "2px solid #f5c400",
											borderRadius: "16px 16px 0px 16px",
											padding: "8px 10px",
											fontSize: 11,
											fontFamily: `${fontFamily || "'Poppins', sans-serif"}`,
											color: contentColor,
											boxShadow: "2px 2px 4px rgba(0,0,0,0.15)",
											width: isSmallScreen ? 170 : 190,
											zIndex: 6,
											lineHeight: 1.35,
										}}
									>
										{section.bubble}
									</Box>
								)}
							</Box>
						);
					}

					const imageSrc = getImageSrc(section.imageId);
					const sectionSticker = getCanvasImageUrl(section.stickerCanvasId);
					return (
						<Box
							key={idx}
							data-block-id={`text-${idx + 1}`}
							sx={{
								position: "relative",
								display: "flex",
								gap: "16px",
								alignItems: "flex-start",
								overflow: "visible",
							}}
						>
							{imageSrc && (
								<Box
									sx={{
										width: "40%",
										position: "relative",
										flexShrink: 0,
										overflow: "visible",
										lineHeight: 0,
									}}
								>
									<img
										src={imageSrc}
										alt={section.title}
										style={{
											width: "100%",
											height: 110,
											objectFit: "cover",
											borderRadius: "12px",
											display: "block",
										}}
									/>
									{sectionSticker && (
										<img
											src={sectionSticker}
											alt=""
											style={{
												width: 95,
												position: "absolute",
												bottom: isSmallScreen ? -240 : -120,
												left: -30,
												zIndex: 5,
											}}
										/>
									)}
									{section.bubble && (
										<Box
											sx={{
												position: "absolute",
												left: 0,
												top: isSmallScreen ? 170 : 80,
												backgroundColor: "#fff8db",
												border: "2px solid #f5c400",
												borderRadius: "16px 16px 16px 0px",
												padding: "8px 10px",
												fontSize: 11,
												fontFamily: `${fontFamily || "'Poppins', sans-serif"}`,
												color: contentColor,
												boxShadow: "2px 2px 4px rgba(0,0,0,0.15)",
												width: isSmallScreen ? 130 : 200,
												zIndex: 6,
												lineHeight: 1.35,
											}}
										>
											{section.bubble}
										</Box>
									)}
								</Box>
							)}
							<Box sx={{ width: imageSrc ? "60%" : "100%" }}>
								{section.title && (
									<Box component="h3" sx={estiloH3}>
										{section.title}
									</Box>
								)}
								{section.body && (
									<Box component="p" sx={estiloP}>
										{section.body}
									</Box>
								)}
							</Box>
							{section.sabiasQue?.map((sq, i) => (
								<Box
									key={i}
									sx={{
										position: "absolute",
										top: -10 + i * 38,
										left: imageSrc ? "calc(40% - 22px)" : 0,
									}}
								>
									<SabiasQue icono={sq.icono} texto={sq.texto} />
								</Box>
							))}
						</Box>
					);
				})}
			</Box>
		</Box>
	);
};

Template338.thumbnail = "/images/template-1.png";

export default Template338;
